import _extends from '@babel/runtime/helpers/extends';
import React__default, { forwardRef, useRef, useEffect, useImperativeHandle } from 'react';
import { useTheme } from 'styled-components';
import { useIsMobile } from '@amzn/storm-ui';
import { f as formatMessageHelper } from './i18n/formatMessageHelper-CMOV131q.js';
import { u as useIntl } from '../LocalModules/storm-ui-intl/dist/esm/3rdPartyModules/react-intl/lib/src/components/useIntl-BGx4v_Uj-CJNeXUZQ.js';
import { u as useFormContext } from '../internalComponents/Form/hooks/useFormContext-DAFplQw7.js';
import { F as FormLayout } from '../internalComponents/FormLayout/FormLayout-D2qp9e0y.js';

const DEFAULT_FEEDBACK_FORM_ERROR_MESSAGE = 'default_ffk_error_message';
const FeedbackFormLayout = /*#__PURE__*/forwardRef(({
  children,
  onClickResubmit,
  onClose,
  cancelButtonLabel: externalCancelButtonLabel,
  closeButtonLabel: externalCloseButtonLabel,
  defaultValidationErrorMessage: externalDefaultValidationErrorMessage,
  mobileCloseButtonLabel: externalMobileCloseButtonLabel,
  mobileShareButtonLabel: externalMobileShareButtonLabel,
  renderHeader: externalRenderHeader,
  renderSuccess: externalRenderSuccess,
  resubmitButtonLabel: externalResubmitButtonLabel,
  submitButtonLabel: externalSubmitButtonLabel,
  submittingButtonLabel: externalSubmittingButtonLabel,
  resetOnClose,
  onLoadSucceeded,
  onFormStateChange,
  ...restProps
}, ref) => {
  var _Object$entries$, _theme$feedbackForm, _theme$feedbackForm2;
  const {
    formatMessage
  } = useIntl();
  const theme = /*#__PURE__*/useTheme();
  const isMobile = useIsMobile();
  const formContext = useFormContext();
  const {
    formState,
    reset,
    submitErrorMessage,
    id,
    handleSubmit
  } = formContext;
  const {
    errors,
    isDirty,
    isSubmitSuccessful,
    isSubmitting,
    isValid,
    isValidating
  } = formState;
  const isSubmitEnabled = isValid && !isValidating;
  const cancelButtonLabel = externalCancelButtonLabel !== null && externalCancelButtonLabel !== void 0 ? externalCancelButtonLabel : formatMessage(formatMessageHelper('cancelButtonLabel'));
  const closeButtonLabel = externalCloseButtonLabel !== null && externalCloseButtonLabel !== void 0 ? externalCloseButtonLabel : formatMessage(formatMessageHelper('closeButtonLabel'));
  const defaultValidationErrorMessage = externalDefaultValidationErrorMessage !== null && externalDefaultValidationErrorMessage !== void 0 ? externalDefaultValidationErrorMessage : formatMessage(formatMessageHelper('defaultValidationErrorMessage'));
  const mobileCloseButtonLabel = externalMobileCloseButtonLabel !== null && externalMobileCloseButtonLabel !== void 0 ? externalMobileCloseButtonLabel : formatMessage(formatMessageHelper('mobileCloseButtonLabel'));
  const mobileShareButtonLabel = externalMobileShareButtonLabel !== null && externalMobileShareButtonLabel !== void 0 ? externalMobileShareButtonLabel : formatMessage(formatMessageHelper('mobileShareButtonLabel'));
  const renderHeader = externalRenderHeader !== null && externalRenderHeader !== void 0 ? externalRenderHeader : formatMessage(formatMessageHelper('renderHeader'));
  const renderSuccess = externalRenderSuccess !== null && externalRenderSuccess !== void 0 ? externalRenderSuccess : formatMessage(formatMessageHelper('renderSuccess'));
  const resubmitButtonLabel = externalResubmitButtonLabel !== null && externalResubmitButtonLabel !== void 0 ? externalResubmitButtonLabel : formatMessage(formatMessageHelper('resubmitButtonLabel'));
  const submitButtonLabel = externalSubmitButtonLabel !== null && externalSubmitButtonLabel !== void 0 ? externalSubmitButtonLabel : formatMessage(formatMessageHelper('submitButtonLabel'));
  const submittingButtonLabel = externalSubmittingButtonLabel !== null && externalSubmittingButtonLabel !== void 0 ? externalSubmittingButtonLabel : formatMessage(formatMessageHelper('submittingButtonLabel'));
  const errorMessage = ((_Object$entries$ = Object.entries(errors)[0]) === null || _Object$entries$ === void 0 || (_Object$entries$ = _Object$entries$[1]) === null || _Object$entries$ === void 0 ? void 0 : _Object$entries$.message) || DEFAULT_FEEDBACK_FORM_ERROR_MESSAGE;
  const validationErrorMessage = isDirty ? `${errorMessage === DEFAULT_FEEDBACK_FORM_ERROR_MESSAGE ? defaultValidationErrorMessage : errorMessage}` : defaultValidationErrorMessage;
  const mountedRef = useRef(false);
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      if (onLoadSucceeded) {
        onLoadSucceeded();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (onFormStateChange) {
      onFormStateChange({
        isDirty,
        isValid,
        isValidating,
        isSubmitEnabled,
        isSubmitSuccessful,
        isSubmitting,
        validationErrorMessage: isSubmitEnabled ? undefined : validationErrorMessage,
        submitErrorMessage
      });
    }
  }, [onFormStateChange, isDirty, isSubmitEnabled, isSubmitting, isSubmitSuccessful, validationErrorMessage, isValid, isValidating, submitErrorMessage]);
  useImperativeHandle(ref, () => ({
    reset() {
      reset();
    },
    submit() {
      if (isSubmitEnabled && !isSubmitting) {
        if (handleSubmit) {
          handleSubmit();
        }
      }
    }
  }), [handleSubmit, isSubmitEnabled, isSubmitting, reset]);
  return /*#__PURE__*/React__default.createElement(FormLayout, _extends({}, restProps, {
    disableSubmitButtonTooltip: isValidating,
    formContext: formContext,
    formId: id,
    isMobile: isMobile !== null && isMobile !== void 0 ? isMobile : false,
    isSubmitSuccessful: isSubmitSuccessful,
    isSubmitting: isSubmitting,
    onClickResubmit: event => {
      reset();
      if (typeof onClickResubmit === 'function') {
        onClickResubmit(event);
      }
    },
    onClose: () => {
      if (resetOnClose) {
        reset();
      }
      if (typeof onClose === 'function') {
        onClose();
      }
    },
    popperPortalElementId: theme === null || theme === void 0 || (_theme$feedbackForm = theme.feedbackForm) === null || _theme$feedbackForm === void 0 || (_theme$feedbackForm = _theme$feedbackForm.popper) === null || _theme$feedbackForm === void 0 ? void 0 : _theme$feedbackForm.portalElementId,
    popperStyle: theme === null || theme === void 0 || (_theme$feedbackForm2 = theme.feedbackForm) === null || _theme$feedbackForm2 === void 0 ? void 0 : _theme$feedbackForm2.popper,
    submitEnabled: isSubmitEnabled,
    submitErrorMessage: submitErrorMessage,
    validationErrorMessage: validationErrorMessage,
    cancelButtonLabel: cancelButtonLabel,
    closeButtonLabel: closeButtonLabel,
    mobileCloseButtonLabel: mobileCloseButtonLabel,
    mobileShareButtonLabel: mobileShareButtonLabel,
    renderHeader: renderHeader,
    renderSuccess: renderSuccess,
    resubmitButtonLabel: resubmitButtonLabel,
    submitButtonLabel: submitButtonLabel,
    submittingButtonLabel: submittingButtonLabel
  }), children);
});

export { DEFAULT_FEEDBACK_FORM_ERROR_MESSAGE as D, FeedbackFormLayout as F };
//# sourceMappingURL=FeedbackFormLayout-DoG7xZaY.js.map
